import { FC, useState } from 'react';
import { Box, Modal, Paper, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton } from '@mui/material';
import { Work, Assignment, Schedule } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { JobUpdateForm, DashboardPageContainer } from '@carcareers/components';
import { JobPreview } from '@carcareers/components';
import { FormProvider } from '@carcareers/contexts';
import { useIsMobile } from '@carcareers/utils';
import { useJobViewQuery } from '@carcareers/store';

export const EmployerJobView: FC = () => {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const jobId = id ? parseInt(id) : 0;
  const { data: job, isLoading } = useJobViewQuery(jobId, { skip: !jobId });
  const [preview, setPreview] = useState<boolean>(false);

  const getJobInitials = (jobTitle?: string) => {
    if (!jobTitle) return 'J';
    return jobTitle
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getDaysAgo = (date?: Date | string) => {
    if (!date) return 0;
    const diffTime = Math.abs(new Date().getTime() - new Date(date).getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getTotalApplications = () => {
    if (!job?.posts) return 0;
    return job.posts.reduce((total, post) => {
      return total + (post.applications?.length || 0);
    }, 0);
  };

  const getActivePostsCount = () => {
    if (!job?.posts) return 0;
    return job.posts.filter((post) => post.status === 'active').length;
  };

  return (
    <DashboardPageContainer idReplacement={job?.title || ''} loading={isLoading}>
      <Modal open={preview} onClose={() => setPreview(false)}>
        <Paper
          sx={{
            overflowY: 'scroll',
            position: 'absolute',
            top: 100,
            left: 100,
            bottom: 100,
            right: 100,
            p: { xs: 2, sm: 2, md: 4 },
          }}
        >
          <JobPreview data={job || {}} />
        </Paper>
      </Modal>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {isLoading ? '' : getJobInitials(job?.title)}
            </Avatar>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {isLoading ? <Skeleton variant="text" width={300} height={32} /> : job?.title || 'Untitled Job'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isLoading ? (
                  <Skeleton variant="text" width={200} height={20} />
                ) : (
                  job?.rooftop?.name || 'No Rooftop Assigned'
                )}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {isLoading ? (
                  <Skeleton variant="rounded" width={120} height={32} />
                ) : (
                  <Chip icon={<Work />} label={`Job ID: ${job?.id || '...'}`} variant="outlined" sx={{ px: 1 }} />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={150} height={32} />
                ) : (
                  <Chip
                    icon={<Assignment />}
                    label={job?.commitmentType || 'Not specified'}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={150} height={32} />
                ) : (
                  <Chip
                    icon={<Schedule />}
                    label={`Created ${new Date(job?.createdAt || '').toLocaleDateString()}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Assignment sx={{ color: 'primary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                job?.posts?.length || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Total Posts
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Work sx={{ color: 'success.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                getActivePostsCount()
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Active Posts
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Assignment sx={{ color: 'secondary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                getTotalApplications()
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Total Applications
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Schedule sx={{ color: 'warning.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                getDaysAgo(job?.createdAt)
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Days Since Created
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Work sx={{ color: 'primary.main' }} />
              Job Details
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <FormProvider initialData={job}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <JobUpdateForm />
              </Box>
            </FormProvider>
          </Box>
        </CardContent>
      </Card>
    </DashboardPageContainer>
  );
};